/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPlayerMatches, type ProfileMatchStatus } from "./profileTabsApi";
import { EmptyState, Segmented } from "./ProfileCommon";
import PlayoffBadge from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/PlayoffBadge";
import { getPlayerImageUrl } from "../../../helper/helper";

const OPTIONS: { key: ProfileMatchStatus; label: string }[] = [
  { key: "IN_PROGRESS", label: "Live" },
  { key: "SCHEDULED", label: "Upcoming" },
  { key: "ENDED", label: "Results" },
];
const EMPTY: Record<ProfileMatchStatus, [string, string]> = {
  IN_PROGRESS: ["No live matches", "Live matches will appear here once they start."],
  SCHEDULED: ["No upcoming matches", "Scheduled matches will appear here once planned."],
  ENDED: ["No match results yet", "Completed matches will appear here once played."],
};
const PAGE = 10;

const scoreOf = (m: any, teamId?: string) => {
  const inn = (m.inningScorecardList ?? []).find((i: any) => i.battingTeamId === teamId);
  return inn ? `${inn.runs}/${inn.wicket} (${inn.over}.${inn.ball ?? 0}/${inn.maxOvers})` : "Yet to bat";
};

const ProfileMatchesTab = ({ playerId }: { playerId: string }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ProfileMatchStatus>("IN_PROGRESS");
  const [items, setItems] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [autoPick, setAutoPick] = useState(true); // Client behaviour: fall through Live → Upcoming → Results while empty

  const load = async (st: ProfileMatchStatus, p: number) => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetchPlayerMatches(playerId, st, p, PAGE);
      const list = res.matchResponseList ?? [];
      if (p === 0 && list.length === 0 && autoPick && st !== "ENDED") {
        setStatus(st === "IN_PROGRESS" ? "SCHEDULED" : "ENDED");
        return;
      }
      setAutoPick(false);
      setItems((prev) => (p === 0 ? list : [...prev, ...list]));
      setHasMore(list.length === PAGE);
      setPage(p);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    void load(status, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, playerId]);

  return (
    <div className="pt-root">
      <Segmented value={status} onChange={(k) => { setAutoPick(false); setStatus(k); }} options={OPTIONS} />
      {error ? (
        <div className="pt-empty"><strong>Something went wrong</strong><button className="pt-retry" type="button" onClick={() => load(status, page)}>Retry</button></div>
      ) : !loading && items.length === 0 ? (
        <EmptyState title={EMPTY[status][0]} hint={EMPTY[status][1]} />
      ) : (
        <div className="pt-match-list">
          {items.map((m) => {
            const teams = [m.teamOne, m.teamTwo];
            const result = m.matchInfo || m.equation || m.matchResult || m.tossResult;
            return (
              <article key={m.matchId} className="pt-match" onClick={() => navigate(`/matches-details/${m.matchId}`)}>
                <div className="pt-match-meta">
                  <span className="pt-pill">{m.matchType || m.tournament?.matchType || "-"}</span>
                  <PlayoffBadge playoffType={m.playoffType} />
                  <span className="pt-muted">{[m.tournament?.tournamentName ?? m.leagueName, m.groundId?.groundName ?? m.city].filter(Boolean).join(" · ")}</span>
                  <span className="pt-muted pt-right">{m.startDate ? new Date(m.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                </div>
                {teams.map((t, i) => (
                  <div className="pt-match-team" key={i}>
                    <span className="pt-team-name">
                      {t?.imageUrl && <img src={getPlayerImageUrl(t.imageUrl) ?? ""} alt="" />}
                      {t?.teamName ?? "-"}
                    </span>
                    <b>{status === "SCHEDULED" ? "" : scoreOf(m, t?.teamId)}</b>
                  </div>
                ))}
                {result && <p className="pt-match-result">{result}</p>}
              </article>
            );
          })}
          {loading && <div className="pt-loading">Loading…</div>}
          {!loading && hasMore && <button type="button" className="pt-retry" onClick={() => load(status, page + 1)}>Load more</button>}
        </div>
      )}
    </div>
  );
};

export default ProfileMatchesTab;
